/**
 * The board resource as the client sees it.
 *
 * A board is a name and a JSON definition describing panels arranged on a
 * canvas and the stages that provide their data. A `BoardRecord` is a board
 * stored on the platform. A `BoardDraft` is a board a person has not saved yet,
 * kept in their browser.
 *
 * Sharing a board is not sharing data. A board holds a query, never a credential
 * and never a snapshot of results, so every viewer's access is re-checked
 * against the topics and sessions the board reads.
 */

/**
 * The values of the backend's `BoardAccessibility` enum, in the lowercase its
 * requests and records use. A record's value follows its owner: `organization`
 * for `org:<org_id>` and `user` for `user:<user_id>`. A created board is
 * personal, and an access request can only name `organization`.
 */
export type BoardAccessibility = "organization" | "user";

/**
 * The longest name the platform stores. Its create and update requests refuse a
 * longer name, and an empty one, so name fields limit input to this length.
 */
export const BOARD_NAME_MAX_LENGTH = 120;

export interface BoardRecord {
  /**
   * Computed by the server from `owner_principal_id`: `"organization"` when the
   * org principal owns the board, `"user"` otherwise.
   */
  accessibility: BoardAccessibility;
  /**
   * The stored JSON object, returned exactly as stored even when it does not
   * parse against the board definition schema. Read `boardDefinitionTitle` for
   * the one key the platform itself reads; validate other content against the
   * board definition schema before use.
   */
  board_definition: Record<string, unknown>;
  board_id: string;
  created: string;
  /** User ID of the board's creator. Ownership is `owner_principal_id`. */
  created_by: string;
  modified: string;
  modified_by: string;
  /**
   * Human-readable name for the board, which the board list and board page show.
   * Unique per owner within an organization, so boards with different owners may
   * share a name.
   */
  name: string;
  org_id: string;
  /**
   * Principal that owns the board, serialized as `ptype:id`: `org:<org_id>` for a
   * board shared with the organization, `user:<user_id>` for a personal one. A
   * board is shared exactly when its owner is the org principal.
   */
  owner_principal_id: string;
  /**
   * How many times definition content has been written, starting at 0. Sharing a
   * board, or renaming one whose definition has no title, leaves it unchanged.
   *
   * A write of definition content sends the revision it was built on as
   * `base_revision` (`updateBoardRequestFor` does this), and the server refuses
   * it when the stored revision has moved on, so it cannot erase an edit saved
   * since.
   */
  revision: number;
}

/**
 * A board that has not been saved: it is kept in the browser of the person who
 * made it, and the platform does not know it. Saving it under a name creates a
 * `BoardRecord` and removes the draft.
 *
 * A draft has the fields board code reads from any board and none of the ones
 * that describe who owns a stored board.
 */
export interface BoardDraft {
  /** Kept and returned exactly as written, as a `BoardRecord`'s is. */
  board_definition: Record<string, unknown>;
  /** A draft id (`isBoardDraftId`), which no stored board has. */
  board_id: string;
  draft: true;
  /** When the draft was created or last written. */
  modified: string;
  /**
   * `null` until the draft is given a name: by an import whose definition has
   * a title, by a fork, or by a title written into its definition. Show a
   * draft's name with `boardDisplayName`.
   */
  name: string | null;
  org_id: string;
  /**
   * How many times definition content has been written, starting at 0. A write
   * built on an older revision is refused, as a `BoardRecord`'s is.
   */
  revision: number;
}

/** A board wherever it is kept. */
export type Board = BoardRecord | BoardDraft;

export function isBoardDraft(board: Board): board is BoardDraft {
  return "draft" in board;
}

/**
 * What a draft with no name is called wherever a board's name is shown, and
 * the name the chat's create tool stores a board under when its definition has
 * no title.
 */
export const UNTITLED_BOARD_NAME = "Untitled board";

/** The name to show for `board`. */
export function boardDisplayName(board: Board): string {
  return board.name ?? UNTITLED_BOARD_NAME;
}

export interface CreateBoardRequest {
  board_definition: Record<string, unknown>;
  name: string;
}

/** A new name for a board, sent without definition content (see `BoardChange`). */
interface BoardRename {
  board_definition?: never;
  name: string;
}

/**
 * The body of `PUT /v1/boards/id/<board_id>`, in the three shapes the server
 * accepts, so a body that breaks the base-revision rule does not compile:
 *
 * 1. Definition content, with the revision it was built on.
 * 2. A name for a board whose stored definition has a title, with the loaded
 *    revision, because keeping the name and that title equal rewrites
 *    definition content.
 * 3. A name for a board whose definition has no title, with no revision, so a
 *    definition save the server stores first cannot fail a name change that
 *    could not have erased it.
 *
 * Build one with `updateBoardRequestFor`, which reads the loaded record to pick
 * between the two rename shapes.
 */
export type UpdateBoardRequest =
  | {
      board_definition: Record<string, unknown>;
      base_revision: number;
      name?: never;
    }
  | (BoardRename & { base_revision: number })
  | (BoardRename & { base_revision?: never });

/**
 * The one change an update carries: replacement definition content, or a new
 * name. Never both, because the platform refuses a request that names the board
 * one thing and titles its definition another; the server keeps the name and
 * the title equal.
 */
export type BoardChange =
  { definition: Record<string, unknown> } | { name: string };

/**
 * The definition's top-level `title` when it is a non-empty string, which the
 * server keeps equal to the record's name, and `null` otherwise.
 *
 * The server applies the same test: a non-empty string counts, and a number,
 * `null`, an empty string or a nested object does not. A `title` of any other
 * value is stored unchanged and does not rename the board.
 */
export function boardDefinitionTitle(
  definition: Record<string, unknown>,
): string | null {
  const title = definition.title;
  if (typeof title === "string" && title !== "") {
    return title;
  }
  return null;
}

/**
 * Whether `value` is an object other than `null` or an array. A board
 * definition must be such an object: the platform cannot store a string, a
 * number or an array as one, so parsed text is checked with this before it
 * becomes a definition.
 */
export function isObjectRecord(
  value: unknown,
): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * The update body that applies `change` to the board the caller loaded.
 *
 * Definition content carries the loaded revision. A rename carries it only when
 * `boardDefinitionTitle` finds a title in the loaded definition, the same test
 * the server applies under its row lock, because only then does the rename
 * rewrite definition content.
 *
 * Definition content goes without a name: the server sets the board's name to
 * the definition's title when it has one, so editing the title is how a person
 * renames a board from the JSON.
 */
export function updateBoardRequestFor(
  loaded: Board,
  change: BoardChange,
): UpdateBoardRequest {
  if ("definition" in change) {
    return {
      board_definition: change.definition,
      base_revision: loaded.revision,
    };
  }
  if (boardDefinitionTitle(loaded.board_definition) !== null) {
    return { name: change.name, base_revision: loaded.revision };
  }
  return { name: change.name };
}

/** Shares a board with its organization. Sharing cannot be reversed. */
export interface UpdateBoardAccessRequest {
  accessibility: "organization";
}

/**
 * The board to keep cached when a write's response arrives: whichever of
 * `existing` and `incoming` was stored later.
 *
 * The higher `revision` is the later one. Sharing a board and renaming an
 * untitled one leave `revision` unchanged and update `modified`, so at equal
 * revisions the later `modified` is the later one. The response to a share or
 * to such a rename therefore replaces the cached board as soon as it arrives,
 * and a definition response that arrives after one, at the same revision, does
 * not undo it. `modified` is compared to the millisecond. Boards equal in
 * both by that comparison are taken as the same stored state, and `incoming`
 * is returned.
 */
export function boardToCache(
  existing: Board | undefined,
  incoming: Board,
): Board {
  if (existing === undefined || existing.revision < incoming.revision) {
    return incoming;
  }
  if (existing.revision > incoming.revision) {
    return existing;
  }
  return Date.parse(existing.modified) > Date.parse(incoming.modified)
    ? existing
    : incoming;
}
